import { useState, useEffect, useCallback } from "react"
import { Link, navigate } from "../lib/router"
import { LayoutDashboard, Users, Check, X, ShieldAlert, LogOut, Menu, Trash2 } from "lucide-react"
import { deleteAccount } from "../lib/api"
import DeleteAccountModal from "../components/DeleteAccountModal"
import { useLanguage } from "../lib/LanguageContext"

const API = process.env.REACT_APP_AUTH_API_URL || "http://localhost:8001"

export default function ClubAdminDashboard() {
  const role = "Club Admin"
  const clubName = localStorage.getItem("clubName") || "My Club"
  const userName = localStorage.getItem("name") || ""
  const [menuOpen, setMenuOpen] = useState(false)
  const closeMenu = () => setMenuOpen(false)
  const { t } = useLanguage()

  const [pendingUsers, setPendingUsers] = useState([])
  const [loading, setLoading] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [deleting, setDeleting] = useState(false)

  const handleDeleteAccount = async () => {
    setDeleting(true)
    if (await deleteAccount()) {
      localStorage.clear()
      navigate("/login")
    } else {
      setDeleting(false)
      setDeleteOpen(false)
    }
  }

  const fetchPending = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/club/pending-coaches`, {
        headers: {
          "Authorization": `Bearer ${localStorage.getItem("token")}`
        }
      })
      if (res.ok) {
        const data = await res.json()
        setPendingUsers(data)
      }
    } catch (e) {
      console.error("Failed to fetch pending approvals", e)
    }
  }, [])

  useEffect(() => {
    fetchPending()
  }, [fetchPending])

  const handleApprove = async (id) => {
    setLoading(true)
    try {
      const res = await fetch(`${API}/api/admin/club/coaches/${id}/approve`, {
        method: "PUT",
        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
      })
      if (res.ok) fetchPending()
    } finally {
      setLoading(false)
    }
  }

  const handleReject = async (id) => {
    setLoading(true)
    try {
      const res = await fetch(`${API}/api/admin/club/coaches/${id}/reject`, {
        method: "PUT",
        headers: { "Authorization": `Bearer ${localStorage.getItem("token")}` }
      })
      if (res.ok) fetchPending()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex flex-col min-h-screen bg-ink-900 lg:flex-row">
      <div>
        {/* Mobile top bar */}
        <div className="flex items-center justify-between border-b border-white/10 bg-ink-800/80 px-4 py-3 lg:hidden">
          <div className="flex items-center gap-2">
            <img src="/vietnam_aquatics.png" alt="Logo" className="h-7 w-auto rounded-md bg-white/95 p-1" onError={(e) => { e.currentTarget.style.display = "none" }} />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400">{role}</span>
          </div>
          <button onClick={() => setMenuOpen(true)} aria-label="Open menu" className="flex h-9 w-9 items-center justify-center rounded border border-white/15 text-paper-0">
            <Menu className="h-5 w-5" />
          </button>
        </div>

        {/* Mobile backdrop */}
        {menuOpen && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={closeMenu} aria-hidden="true" />}

        <aside
          className={`fixed inset-y-0 left-0 z-50 flex w-72 max-w-[85vw] shrink-0 flex-col overflow-y-auto border-r border-white/10 bg-ink-800 p-6 transition-transform duration-300 lg:sticky lg:top-0 lg:z-auto lg:h-screen lg:w-64 lg:max-w-none lg:translate-x-0 lg:bg-ink-800/50 ${
            menuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
        >
          <button onClick={closeMenu} aria-label="Close menu" className="mb-6 flex h-11 w-11 items-center justify-center self-end text-mist-300 lg:hidden">
            <X className="h-5 w-5" />
          </button>

          <div className="mb-10 flex flex-col items-center gap-4 text-center">
            <img
              src="/vietnam_aquatics.png"
              alt="Logo"
              className="h-16 w-auto rounded-xl bg-white/95 p-2"
              onError={(e) => { e.currentTarget.style.display = "none" }}
            />
            <div className="flex flex-col items-center gap-1.5 mt-2">
              <span className="text-xs font-bold uppercase tracking-[0.25em] text-gold-400">{role}</span>
              {clubName && <span className="text-2xl font-bold text-paper-0">{clubName}</span>}
              {userName && <span className={`font-bold tracking-tight ${clubName ? 'text-base text-mist-300' : 'text-2xl text-paper-0'}`}>{userName}</span>}
            </div>
          </div>

          <nav className="flex flex-col gap-2 flex-1">
            <Link to="/club-admin/dashboard" onClick={closeMenu} className="flex items-center gap-3 rounded bg-gold-500/10 px-4 py-2.5 text-base font-semibold text-gold-400">
              <LayoutDashboard className="h-5 w-5" />
              {t("nav_dashboard")}
            </Link>
            <Link to="/club-admin/my-club" onClick={closeMenu} className="flex items-center gap-3 rounded px-4 py-2.5 text-base font-medium text-mist-300 transition-colors hover:bg-white/5 hover:text-paper-0">
              <Users className="h-5 w-5" />
              {t("nav_my_club")}
            </Link>
          </nav>

          <div className="mt-auto border-t border-white/10 pt-6">
            <Link to="/login" onClick={() => { localStorage.clear(); closeMenu() }} className="group flex w-full items-center gap-3 rounded border border-red-500/20 bg-red-500/5 px-4 py-2.5 text-base font-semibold text-red-400 transition-all duration-300 hover:border-red-500/40 hover:bg-red-500 hover:text-white hover:shadow-[0_0_15px_rgba(239,68,68,0.25)]">
              <div className="flex items-center justify-center transition-transform group-hover:-translate-x-1">
                <LogOut className="h-5 w-5" />
              </div>
              {t("nav_sign_out")}
            </Link>
            <button
              onClick={() => setDeleteOpen(true)}
              className="mt-2 flex w-full items-center gap-3 rounded px-4 py-2.5 text-base font-medium text-mist-500 transition-colors hover:text-red-400"
            >
              <Trash2 className="h-5 w-5" />
              {t("nav_delete_account")}
            </button>
          </div>
        </aside>
      </div>

      <DeleteAccountModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteAccount}
        busy={deleting}
      />

      <main className="flex-1 p-4 sm:p-6 lg:p-10 overflow-y-auto">
        <header className="mb-10">
          <h1 className="font-display text-3xl font-bold tracking-tight text-paper-0">{t("club_admin_dash_welcome")}</h1>
          <p className="mt-1 text-sm text-mist-300">{t("club_admin_dash_overview")}</p>
        </header>

        <div className="rounded border border-white/10 bg-ink-800/30">
          <div className="flex items-center gap-3 border-b border-white/10 p-5">
            <ShieldAlert className="h-5 w-5 text-gold-400" />
            <h2 className="font-display text-lg font-bold text-paper-0">
              {t("club_admin_dash_pending_coaches")}
            </h2>
          </div>
          
          <div className="p-5">
            {pendingUsers.length === 0 ? (
              <p className="text-mist-400 text-sm">{t("club_admin_dash_no_pending")}</p>
            ) : (
              <div className="flex flex-col gap-3">
                {pendingUsers.map(user => (
                  <div key={user.id} className="flex items-center justify-between rounded border border-white/10 bg-ink-800/50 p-4 transition-colors hover:bg-white/5">
                    <div>
                      <p className="font-medium text-paper-0">{user.name}</p>
                      <p className="text-sm text-mist-400">{t("club_admin_dash_applied")}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button 
                        disabled={loading}
                        onClick={() => handleReject(user.id)}
                        className="flex h-8 w-8 items-center justify-center rounded border border-red-500/30 text-red-400 hover:bg-red-500/10 disabled:opacity-50"
                      >
                        <X className="h-4 w-4" />
                      </button>
                      <button 
                        disabled={loading}
                        onClick={() => handleApprove(user.id)}
                        className="flex h-8 items-center gap-1.5 rounded bg-gold-500 px-3 text-xs font-semibold text-ink-900 hover:bg-gold-400 disabled:opacity-50"
                      >
                        <Check className="h-3.5 w-3.5" />
                        {t("club_admin_dash_approve")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
